import { useEffect } from "react";

/**
 * Lightweight Client-Side Error Tracker
 *
 * Captures unhandled errors and promise rejections, storing the last 50
 * entries in localStorage. Use getErrorLog() and clearErrorLog() to
 * inspect or reset collected errors.
 *
 * No external dependencies -- a simple alternative to Sentry for
 * low-traffic sites or early-stage projects.
 */

const STORAGE_KEY = "error_log";
const MAX_ERRORS = 50;

/**
 * Retrieve all captured errors from localStorage.
 * @returns {Array<Object>} Array of error entries
 */
export function getErrorLog() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Clear the stored error log.
 */
export function clearErrorLog() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // silently fail
  }
}

/**
 * Persist a new error entry, keeping only the most recent MAX_ERRORS.
 */
function pushError(entry) {
  try {
    const log = getErrorLog();
    log.push(entry);
    // Keep only the last MAX_ERRORS entries
    const trimmed = log.slice(-MAX_ERRORS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage may be full or unavailable -- silently fail
  }
}

const ErrorTracker = () => {
  useEffect(() => {
    const handleError = (event) => {
      pushError({
        type: "error",
        message: event.message || "Unknown error",
        stack: event.error?.stack || null,
        filename: event.filename || null,
        lineno: event.lineno || null,
        colno: event.colno || null,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });
    };

    const handleRejection = (event) => {
      const reason = event.reason;
      pushError({
        type: "unhandledrejection",
        message:
          reason instanceof Error
            ? reason.message
            : typeof reason === "string"
              ? reason
              : "Unhandled promise rejection",
        stack: reason instanceof Error ? reason.stack : null,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  // This component only sets up listeners; it renders nothing.
  return null;
};

export default ErrorTracker;
